// Every write the app can make, defined once and shared by the server and the browser.
//
// - `create` / `update` / `remove` mutations need no server code: the server inserts, patches or
//   deletes the row, records the audit entry, and the browser shows the change instantly
//   (optimistic) because it knows the table and the row.
// - `custom` mutations need an implementation in server/src/mutations/ (the server won't boot
//   without one).
// - `rules` in server/src/mutations/ can add checks to any mutation too.
import { z } from "zod";
import { ADMIN, create, custom, ROLES, type Role, remove, STAFF, update } from "./mutation-kit.ts";
import { billMutations } from "./mutations-bills.ts"; // Bills and payments ---
import { chargeMutations } from "./mutations-charges.ts"; // Charges ---
import { consignmentMutations } from "./mutations-consignment.ts"; // Consignment ---
import { countMutations } from "./mutations-counts.ts"; // Stock counts ---
import { makeMutations } from "./mutations-make.ts"; // Make planning ---
import { partGroupMutations } from "./mutations-part-groups.ts"; // Part groups ---
import { planningMutations } from "./mutations-planning.ts";
import { poDocsMutations } from "./mutations-po-docs.ts"; // PO documents ---
import { poEmailMutations } from "./mutations-po-email.ts"; // PO email + landed cost ---
import { planCheckSeen, poPlanCheckMutations } from "./mutations-po-plan-check.ts"; // Check against the plan ---
import { portalMutations } from "./mutations-portal.ts"; // Vendor portal ---
import { presaleMutations } from "./mutations-presales.ts"; // Pre-sales ---
import { projectMutations, sectionName } from "./mutations-projects.ts"; // Manufacturing projects ---
import { receivingMutations } from "./mutations-receiving.ts"; // Receiving ---
import { runMutations } from "./mutations-runs.ts"; // Production runs ---
import { specMutations } from "./mutations-specs.ts"; // Specs ---
import { PO_STATUSES, planBasisSchema, poCreateArgs, poFields, poLineFields, poLineInput, shippingInput, shipToFields, vendorPoFields } from "./po.ts";
import { PORTAL_LANGS } from "./portal.ts"; // Vendor portal ---
import { PACK_PRINTS, PACK_TYPES, PART_TYPES } from "./projects.ts"; // Manufacturing projects ---

export { ROLES, type Role, type TableName } from "./mutation-kit.ts";

const EVERYONE = ROLES;

const text = (max = 200) => z.string().trim().max(max);
const name = z.string().trim().min(1, "Name is required").max(200);
const money = z.number().nonnegative("Must be 0 or more").nullable();
const isoDate = z.iso.date("Use a date like 2026-12-31").nullable();

// Fields ----------------------------------------------------------------------------------------

export const userFields = z.object({
  email: z.email("Enter a valid email").trim().toLowerCase(),
  name: text(),
  role: z.enum(ROLES),
  active: z.boolean(),
  // Bills and payments --- may create payments (Wise transfers, payments recorded or linked); admins set it
  can_pay: z.boolean(),
});

// Table views --- a table's layout: columns in order (by key), the ones hidden, and widths someone dragged (px)
const columnKey = z.string().min(1).max(100);
export const tableLayout = z.object({
  order: z.array(columnKey).max(100).optional(),
  hidden: z.array(columnKey).max(100).optional(),
  shown: z.array(columnKey).max(100).optional(),
  widths: z.record(columnKey, z.number().int().min(40).max(2000)).optional(),
});
const tableSort = z.object({ key: columnKey, desc: z.boolean().optional() }).nullable();
const tableViewFields = z.object({
  table_key: z.string().trim().min(1).max(300),
  name: z.string().trim().min(1, "Name the view").max(60, "Keep a view's name under 60 characters"),
  layout: tableLayout,
  sort: tableSort,
  search: z.string().max(2000),
  position: z.number().int(),
});
// Cover through (migration 061, shared/cover.ts): a person's own, for every vendor (vendor_id null) or one; days or a date.
const planCoverFields = z.object({
  vendor_id: z.uuid().nullable(),
  days: z.number().int().min(1, "Pick a day after today").max(730, "Up to two years ahead").nullable(),
  through: isoDate,
});
const tablePrefFields = z.object({
  table_key: z.string().trim().min(1).max(300),
  view_id: z.uuid().nullable(),
  layout: tableLayout,
  sort: tableSort,
});

export const VENDOR_KINDS = ["supplier", "contract_mfr", "both"] as const;
export const vendorFields = z.object({
  name,
  short_name: text(50),
  kind: z.enum(VENDOR_KINDS),
  currency: z.string().trim().toUpperCase().length(3, "Use a 3-letter currency code"),
  payment_terms: text(),
  incoterms: text(50),
  website: text(500),
  timezone: text(100),
  language: text(20),
  notes: text(20_000),
  archived: z.boolean(),
  // Forecasting --- defaults for vendor links that have none (the purchasing plan)
  lead_time_days: z.number().int("Use whole days").min(0, "Use 0 to 730 days").max(730, "Use 0 to 730 days").nullable(),
  moq: z.number().nonnegative("MOQ must be 0 or more").max(10_000_000).nullable(),
  order_cycle_days: z.number().int("Use whole days").min(1, "Use 1 to 730 days").max(730, "Use 1 to 730 days").nullable(),
  // Other names this vendor goes by (Shopify's vendor field, old spellings); the Shopify vendor job matches them
  aliases: z.array(z.string().trim().min(1).max(200)).max(50),
  // Vendor closures --- where it is (ISO 3166 alpha-2, e.g. CN), so its country's closures (Chinese New Year) apply
  country: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^([A-Z]{2})?$/, "Use a 2-letter country code, e.g. CN"),
});

export const PROJECT_STATUSES = ["npi", "active", "eol", "archived"] as const;
export const projectFields = z.object({
  name,
  code: z.string().trim().toUpperCase().max(20),
  status: z.enum(PROJECT_STATUSES),
  color: text(20),
  notes: text(20_000),
  // Manufacturing projects --- basics: who builds it, when it launches, who owns it, the setup it started from
  cm_vendor_id: z.uuid().nullable(),
  launch_date: isoDate,
  owner_id: z.uuid().nullable(),
  template: text(50),
  // Production runs --- what duty is charged on: materials + assembly ('full', the parts HHL supplied too) or the CM's invoice
  customs_basis: z.enum(["full", "cm_invoice"]),
});

export const ITEM_KINDS = ["sell", "material", "subassembly", "service"] as const;
export const LOCAL_ITEM_KINDS = ["material", "subassembly", "service"] as const;
export const PROCUREMENTS = ["buy", "make", "both"] as const;
export const LIFECYCLES = ["npi", "active", "eol", "obsolete"] as const;
export const itemFields = z.object({
  kind: z.enum(LOCAL_ITEM_KINDS),
  sku: z.string().trim().toUpperCase().max(64),
  name,
  description: text(20_000),
  uom: text(20),
  barcode: text(64),
  photo_url: text(1000),
  procurement: z.enum(PROCUREMENTS),
  std_cost: money,
  weight_g: money,
  hs_code: text(20),
  country_of_origin: z.string().trim().toUpperCase().max(2),
  project_id: z.uuid().nullable(),
  tags: z
    .array(z.string().trim().toLowerCase().min(1).max(40))
    .max(30)
    .transform((tags) => [...new Set(tags)]),
  lifecycle: z.enum(LIFECYCLES),
  notes: text(20_000),
  archived: z.boolean(),
  part_type: z.enum(PART_TYPES), // Manufacturing projects --- drives the files a part's spec should have
  // Packaging --- a packaging part's kind and printing ('' = not said / the usual for its kind): its print files
  pack_type: z.enum(["", ...PACK_TYPES]),
  pack_print: z.enum(["", ...PACK_PRINTS]),
  // Product labels --- a generated label's size and the line every product's label carries
  label_w_mm: z.number().min(10, "At least 10 mm").max(400, "At most 400 mm").nullable(),
  label_h_mm: z.number().min(10, "At least 10 mm").max(400, "At most 400 mm").nullable(),
  label_note: text(300),
});
/** Fields the Shopify sync owns on mirrored items; edit those in Shopify. */
export const SHOPIFY_OWNED_FIELDS = [
  "kind",
  "sku",
  "name",
  "barcode",
  "photo_url",
  "std_cost",
  "weight_g",
  "hs_code",
  "country_of_origin",
  "archived",
] as const;

export const vendorContactFields = z.object({
  vendor_id: z.uuid(),
  name: text(),
  role: text(),
  email: z.union([z.literal(""), z.email("Enter a valid email").trim().toLowerCase()]),
  phone: text(50),
  wechat: text(100),
  is_po_recipient: z.boolean(),
  // Vendor portal --- signs in at /v (needs an email), in this language
  portal: z.boolean(),
  portal_lang: z.enum(PORTAL_LANGS),
  notes: text(5000),
});

export const ADDRESS_KINDS = ["billing", "ship_from", "factory", "remit", "other"] as const;
export const vendorAddressFields = z.object({
  vendor_id: z.uuid(),
  kind: z.enum(ADDRESS_KINDS),
  label: text(),
  line1: text(),
  line2: text(),
  district: text(), // Addresses --- China's 区 (migration 077)
  city: text(),
  region: text(),
  postal_code: text(30),
  country: z.string().trim().toUpperCase().max(2),
  notes: text(5000),
});

export const vendorItemFields = z.object({
  vendor_id: z.uuid(),
  item_id: z.uuid(),
  vendor_sku: text(100),
  vendor_name: text(300),
  unit_price: money,
  currency: z.string().trim().toUpperCase().length(3, "Use a 3-letter currency code"),
  price_breaks: z.array(z.object({ min_qty: z.number().positive(), unit_price: z.number().nonnegative() })).max(20),
  moq: z.number().nonnegative(),
  order_multiple: z.number().positive("Must be more than 0"),
  lead_time_days: z.number().int().nonnegative().nullable(),
  quote_ref: text(),
  quote_expires: isoDate,
  is_preferred: z.boolean(),
  notes: text(5000),
});

export const locationFields = z.object({ ignored: z.boolean(), notes: text(5000) });

// Catalog editor: fields HHL Ops can change in Shopify. Names are HHL Ops' own (item columns for
// variants, shopify_product columns for products); the server maps them to Shopify's API.
export const variantEditFields = z.object({
  price: z.number().nonnegative("Price must be 0 or more"),
  compare_at_price: money,
  sku: z.string().trim().max(255),
  barcode: text(64),
  std_cost: money,
  weight_g: money,
  hs_code: z
    .string()
    .trim()
    .regex(/^(\d{6,13})?$/, "HS codes are 6 to 13 digits"),
  country_of_origin: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^([A-Z]{2})?$/, "Use a 2-letter country code, like CN"),
  shopify_tracked: z.boolean(),
});
export const PRODUCT_STATUSES = ["ACTIVE", "DRAFT", "ARCHIVED", "UNLISTED"] as const;
export const productEditFields = z.object({
  title: z.string().trim().min(1, "Title is required").max(255),
  status: z.enum(PRODUCT_STATUSES),
  tags: z
    .array(z.string().trim().min(1).max(255))
    .max(250)
    .transform((tags) => [...new Set(tags)]),
  vendor: text(255),
  product_type: text(255),
});
export const SHOPIFY_PRODUCT_ID = z.string().regex(/^gid:\/\/shopify\/Product\/\d+$/, "Not a Shopify product id");

// BOMs ---
// A family per Shopify product or sub-assembly; each bom row is a revision. Only drafts change (lines,
// option maps, overrides); bom.revise copies a revision into a new draft, bom.release makes it active.
// Resolution and costs: shared/boms.ts. Rules: server/src/mutations/boms.ts.
export const BOM_SUPPLIES = ["hhl_consigns", "cm_buys", "cm_makes"] as const;
/** {"Connection type": ["Wireless"]}: which variants a line is in ({} = all). */
export const bomConditions = z
  .record(z.string().trim().min(1).max(100), z.array(z.string().trim().min(1).max(200)).min(1).max(200))
  .refine((c) => Object.keys(c).length <= 10, "Up to 10 options per line");
const bomQty = z.number().nonnegative("Quantity must be 0 or more").max(1_000_000);
export const bomFields = z.object({
  // Its own projects: a Shopify product's BOM names one at most (the finished item), a kit or a board nothing uses several
  project_ids: z.array(z.uuid()).max(20),
  make_vendor_id: z.uuid().nullable(),
  phantom: z.boolean(),
  notes: text(20_000),
  section_order: z.array(sectionName.min(1)).max(40), // Manufacturing projects --- module order (drafts only)
});
export const bomLineFields = z.object({
  bom_id: z.uuid(),
  position: z.number().int().min(0).max(100_000),
  component_item_id: z.uuid().nullable(),
  qty: bomQty,
  scrap_pct: z.number().min(0, "Scrap must be 0 or more").max(99, "Scrap must be under 100%"),
  ref_designator: text(500),
  is_packaging: z.boolean(),
  supply: z.enum(BOM_SUPPLIES),
  applies_when: bomConditions,
  option_name: text(100),
  notes: text(5000),
  section: sectionName, // Manufacturing projects --- the line's module
});
export const bomOptionMapFields = z.object({
  bom_line_id: z.uuid(),
  option_value: z.string().trim().min(1, "Pick a value").max(200),
  component_item_id: z.uuid(),
});
export const bomOverrideFields = z.object({
  bom_line_id: z.uuid(),
  variant_item_id: z.uuid(),
  qty: bomQty.nullable(),
  component_item_id: z.uuid().nullable(),
});
const bomMutations = {
  // Owner: shopify_product_id or item_id (a sub-assembly). The browser numbers the revision; the server checks it.
  "bom.create": create(
    "bom",
    bomFields.partial().extend({
      shopify_product_id: SHOPIFY_PRODUCT_ID.nullable().optional(),
      item_id: z.uuid().nullable().optional(),
      revision: z.number().int().positive(),
    }),
    STAFF,
  ),
  "bom.update": update("bom", bomFields, STAFF),
  /** Deletes a draft revision and its lines. */
  "bom.remove": remove("bom", STAFF),
  /** Copies a revision (lines, option maps, overrides) into a new draft with the next number. */
  "bom.revise": custom(z.object({ id: z.uuid(), from: z.uuid() }), STAFF),
  /**
   * Who supplies a line's part to the build's maker (Mitch, 2026-11-02: "the info of where a part is bought or made is
   * spread out … this needs to be cleaned up"). Sourcing, not part of a revision: it changes on every revision of the
   * family that isn't retired (the same part's lines), never starting a draft. Returns { bom_id, revision, changed }.
   */
  "bom.set_line_supply": custom(z.object({ line_id: z.uuid(), supply: z.enum(BOM_SUPPLIES) }), STAFF),
  /** Who makes the build (the BOM's maker; null: whoever builds what uses it), on its revisions the same way. */
  "bom.set_maker": custom(z.object({ bom_id: z.uuid(), make_vendor_id: z.uuid().nullable() }), STAFF),
  /** Draft → active from a date (default today); once in effect, the family's other active revisions retire. */
  /**
   * Releases the BOM draft as the item's next revision (server/src/item-release.ts): a sub-assembly's spec goes with it
   * under the same number. `note`: what changed, needed for every release but the first.
   */
  "bom.release": custom(z.object({ id: z.uuid(), effective_from: isoDate.optional(), note: z.string().trim().max(5000).optional() }), STAFF),
  "bom.retire": custom(z.object({ id: z.uuid() }), STAFF),
  /** Active → draft again, for a revision no production has used (no make order built from it, no run pinned to it). */
  "bom.unrelease": custom(z.object({ id: z.uuid() }), STAFF),
  /**
   * Swaps one part for another in the chosen revisions (each family's newest draft, changed in place, or
   * the revision in use, copied into a new draft first; an active revision never changes). effective_from
   * is when it should take effect: kept on each draft for its release, or used now when `release` is set.
   */
  "bom.replace_part": custom(
    z.object({
      from_item_id: z.uuid(),
      to_item_id: z.uuid(),
      bom_ids: z.array(z.uuid()).min(1, "Pick at least one BOM").max(500),
      effective_from: isoDate.optional(),
      release: z.boolean().optional(),
    }),
    STAFF,
  ),
  "bom_line.create": create("bom_line", bomLineFields.partial().required({ bom_id: true }), STAFF),
  "bom_line.update": update("bom_line", bomLineFields.omit({ bom_id: true }), STAFF),
  "bom_line.remove": remove("bom_line", STAFF),
  "bom_option_map.create": create("bom_option_map", bomOptionMapFields, STAFF),
  "bom_option_map.update": update("bom_option_map", bomOptionMapFields.omit({ bom_line_id: true }), STAFF),
  "bom_option_map.remove": remove("bom_option_map", STAFF),
  "bom_override.create": create("bom_override", bomOverrideFields.partial().required({ bom_line_id: true, variant_item_id: true }), STAFF),
  "bom_override.update": update("bom_override", bomOverrideFields.omit({ bom_line_id: true, variant_item_id: true }), STAFF),
  "bom_override.remove": remove("bom_override", STAFF),
} as const;

// Mutations -------------------------------------------------------------------------------------

export const mutations = {
  "user.create": create("app_user", userFields.partial().required({ email: true, role: true }), ADMIN),
  "user.update": update("app_user", userFields.omit({ email: true }), ADMIN),
  /**
   * A workstation (migration 072): a shared account with no email for a PC (the shippers' station), always Receiving,
   * never Can pay. A PC signs in as it with a one-time code (`user.workstation_code`).
   */
  "user.create_workstation": custom(z.object({ id: z.uuid(), name: z.string().trim().min(1, "Name it (Shipping workstation)").max(100) }), ADMIN),
  /** A one-time code to sign a PC in as this workstation (15 minutes; a new one replaces any not used). Returns { code, expires_at }. */
  "user.workstation_code": custom(z.object({ id: z.uuid() }), ADMIN),
  /** Signs every computer signed in as this user out (a lost or replaced workstation PC); codes not used yet stop working. */
  "user.sign_out_everywhere": custom(z.object({ id: z.uuid() }), ADMIN),

  // Table views --- each person's own (the server sets and checks user_id: server/src/mutations/table-views.ts)
  "table_view.create": create("table_view", tableViewFields.partial().required({ table_key: true, name: true }), EVERYONE),
  "table_view.update": update("table_view", tableViewFields.omit({ table_key: true }), EVERYONE),
  "table_view.remove": remove("table_view", EVERYONE),
  "table_pref.create": create("table_pref", tablePrefFields.partial().required({ table_key: true }), EVERYONE),
  "table_pref.update": update("table_pref", tablePrefFields.omit({ table_key: true }), EVERYONE),
  // Cover through --- the signed-in person's own (server/src/mutations/table-views.ts sets and checks user_id)
  "plan_cover.create": create("plan_cover", planCoverFields, EVERYONE),
  "plan_cover.update": update("plan_cover", planCoverFields.omit({ vendor_id: true }), EVERYONE),
  "plan_cover.remove": remove("plan_cover", EVERYONE),
  // Suggested POs on Today --- a vendor's card hidden (until null) or snoozed until a day; the signed-in person's own
  "suggested_po_hidden.create": create("suggested_po_hidden", z.object({ vendor_id: z.uuid(), until: isoDate }), EVERYONE),
  "suggested_po_hidden.update": update("suggested_po_hidden", z.object({ until: isoDate }), EVERYONE),
  "suggested_po_hidden.remove": remove("suggested_po_hidden", EVERYONE),

  "vendor.create": create("vendor", vendorFields.partial().required({ name: true }), STAFF),
  "vendor.update": update("vendor", vendorFields, STAFF),
  // Moves everything of from_id (items, POs, contacts, addresses, sites, BOMs) to into_id, keeps its names as aliases, then deletes it
  "vendor.merge": custom(z.object({ from_id: z.uuid(), into_id: z.uuid() }), STAFF),
  "vendor_contact.create": create("vendor_contact", vendorContactFields.partial().required({ vendor_id: true }), STAFF),
  "vendor_contact.update": update("vendor_contact", vendorContactFields.omit({ vendor_id: true }), STAFF),
  "vendor_contact.remove": remove("vendor_contact", STAFF),
  "vendor_address.create": create("vendor_address", vendorAddressFields.partial().required({ vendor_id: true }), STAFF),
  "vendor_address.update": update("vendor_address", vendorAddressFields.omit({ vendor_id: true }), STAFF),
  "vendor_address.remove": remove("vendor_address", STAFF),
  "vendor_item.create": create("vendor_item", vendorItemFields.partial().required({ vendor_id: true, item_id: true }), STAFF),
  "vendor_item.update": update("vendor_item", vendorItemFields.omit({ vendor_id: true, item_id: true }), STAFF),
  "vendor_item.remove": remove("vendor_item", STAFF),

  "project.create": create("project", projectFields.partial().required({ name: true }), STAFF),
  "project.update": update("project", projectFields, STAFF),

  // Shopify owns a location's name and status; HHL Ops only decides whether to use it.
  "location.update": update("location", locationFields, ADMIN),

  // Every item made here is a part, and a part is in the projects whose BOMs use it: a project sent is dropped.
  "item.create": create("item", itemFields.omit({ project_id: true }).partial().required({ kind: true, name: true }), STAFF),
  // For Shopify items only the local fields (project, tags, procurement, lifecycle, notes, uom, description) change;
  // a part's project is refused (itemPatchRules()).
  "item.update": update("item", itemFields, STAFF),

  // Catalog editor: change HHL Ops' copy now and queue the change for Shopify (shopify_write).
  "shopify.edit_variants": custom(
    z.object({
      edits: z
        .array(z.object({ item_id: z.uuid(), patch: variantEditFields.partial() }))
        .min(1)
        .max(2000),
    }),
    STAFF,
  ),
  "shopify.edit_products": custom(
    z.object({
      edits: z
        .array(z.object({ product_id: SHOPIFY_PRODUCT_ID, patch: productEditFields.partial() }))
        .min(1)
        .max(1000),
    }),
    STAFF,
  ),
  /** Sends a failed edit again, or drops it (HHL Ops already shows Shopify's value again). */
  "shopify.retry_write": custom(z.object({ id: z.uuid() }), STAFF),
  "shopify.dismiss_write": custom(z.object({ id: z.uuid() }), STAFF),

  // Returns the token once; only its hash is stored.
  "token.create": custom(z.object({ id: z.uuid(), name: z.string().trim().min(1, "Give the token a name").max(100) }), EVERYONE),
  "token.revoke": custom(z.object({ id: z.uuid() }), EVERYONE),

  // BOMs ---
  ...bomMutations,
  // Purchase orders --------------------------------------------------------------------------------
  // Rules and implementations: server/src/mutations/purchase-orders.ts; fields and shared rules: shared/po.ts.
  /** A draft PO with the vendor's terms, optionally with lines (defaults from each item's vendor link). Returns { id, number }. */
  "po.create": custom(poCreateArgs, STAFF),
  "po.update": update("po", poFields, STAFF),
  /** Only drafts that were never sent; cancel the others. */
  "po.remove": remove("po", STAFF),
  "po.add_lines": custom(z.object({ po_id: z.uuid(), lines: z.array(poLineInput).min(1).max(500) }), STAFF),
  /**
   * Two drafts to one vendor become one (Mitch, 2026-11-03): `from_id`'s lines go onto `into_id` (an item on both is
   * one line with both quantities), its notes after into's, and `from_id` goes. Only purchase drafts never sent, to the
   * same vendor and ship-to. Returns { number, moved, raised }.
   */
  "po.merge": custom(z.object({ from_id: z.uuid(), into_id: z.uuid() }), STAFF),
  /**
   * The Purchasing plan's lines onto a vendor's open draft, all or none, and only while it's still a draft: new items
   * as lines (with their plan basis), and an item already on it raised to `qty` (its new total) with a new basis.
   */
  "po.add_from_plan": custom(
    z.object({
      po_id: z.uuid(),
      lines: z.array(poLineInput).max(500),
      raise: z.array(z.object({ line_id: z.uuid(), item_id: z.uuid(), qty: z.number().positive(), plan_basis: planBasisSchema.optional() })).max(500),
      /** Cover through --- the day the lines were planned to last to (null: the plan's usual); the draft takes it. */
      cover_through: z.iso.date().nullable().optional(),
    }),
    STAFF,
  ),
  "po_line.update": update("po_line", poLineFields, STAFF),
  "po_line.remove": remove("po_line", STAFF),
  /** Moves a PO along its lifecycle (NEXT_STATUSES in shared/po.ts). */
  // Inbound tracking --- moving to shipped can record how it travels (carrier, tracking number, shipped date).
  "po.set_status": custom(z.object({ id: z.uuid(), status: z.enum(PO_STATUSES), shipping: shippingInput.optional() }), STAFF),
  /**
   * The vendor has the current revision: a draft becomes sent; sent_at and sent_revision are recorded. `plan_check`:
   * sent although the check against the plan showed differences (what the warning said is kept on the PO).
   */
  "po.mark_sent": custom(z.object({ id: z.uuid(), plan_check: planCheckSeen.optional() }), STAFF),
  /** A vendor's site POs can ship to (a CM holding consigned parts). HHL's locations come from Shopify. */
  "location.create_site": create(
    "location",
    shipToFields
      .partial()
      .required({ name: true })
      .extend({ kind: z.literal("vendor_site"), vendor_id: z.uuid() }),
    STAFF,
  ),
  "location.update_address": update("location", shipToFields, STAFF),
  "vendor.update_po_defaults": update("vendor", vendorPoFields, STAFF),
  // Planning ---
  ...planningMutations,
  ...receivingMutations, // Receiving ---
  ...specMutations, // Specs ---
  ...poDocsMutations, // PO documents ---
  ...makeMutations, // Make planning ---
  ...consignmentMutations, // Consignment --- CM sites, consign shipments, make orders, counts
  ...poEmailMutations, // PO email + landed cost ---
  ...projectMutations, // Manufacturing projects --- product links, part types, BOM modules
  ...poPlanCheckMutations, // Check against the plan ---
  ...runMutations, // Production runs --- runs, costs, tooling, timeline, attachments, rates, the Shopify cost push
  ...billMutations, // Bills and payments --- bills, 3-way match, payments recorded outside, Wise history links
  ...presaleMutations, // Pre-sales --- campaigns, reservations, holds, income, shipments to backers
  // Part groups --- parts that differ by colour or model, grouped; a shared file copied to every member
  ...partGroupMutations,
  ...portalMutations, // Vendor portal --- people at vendors, their sign-in links, linking their products to parts
  ...chargeMutations, // Charges --- what a maker charges for a build: a BOM's, and a make PO line's for its order
  ...countMutations, // Stock counts --- the day's list to count by hand, and each count
  /**
   * Changes every member of a part group as item.update would change each one (its rules run per part): the way to
   * change a member's type or part type, which a group's parts all share. Returns { changed }.
   */
  "part_group.set_all": custom(
    z.object({
      id: z.uuid(),
      patch: itemFields
        .pick({
          kind: true,
          part_type: true,
          uom: true,
          procurement: true,
          pack_type: true,
          pack_print: true,
          label_w_mm: true,
          label_h_mm: true,
          label_note: true,
          hs_code: true,
          country_of_origin: true,
          weight_g: true,
        })
        .partial(),
    }),
    STAFF,
  ),
} as const;

export type Mutations = typeof mutations;
export type MutationName = keyof Mutations;
export type MutationArgs<N extends MutationName> = z.infer<Mutations[N]["args"]>;
export type CustomMutationName = { [N in MutationName]: Mutations[N]["kind"] extends "custom" ? N : never }[MutationName];

export const canRun = (name: MutationName, role: Role | undefined) => !!role && (mutations[name].roles as readonly Role[]).includes(role);
